import {
    COMPACT_SHAPE_BEARING_X10,
    COMPACT_SHAPE_CUM_DIST,
    COMPACT_SHAPE_LAT,
    COMPACT_SHAPE_LON,
    COMPACT_SHAPE_STRIDE,
} from "./compactConstants";

/**
 * Zero-copy sub-range of a {@link CompactShape} buffer.
 *
 * Created by {@link CompactShape.slice} for a trip segment. Timeline code uses **local**
 * indices (`0 .. length-1`) when walking vertices between stops; schedule `shape_idx` values
 * are converted with {@link globalIndex} (or by subtracting `offset`).
 */
export class CompactShapeView {

    /**
     * @param data Parent shape `Float64Array` (shared, not copied).
     * @param offset Global point index of local index 0.
     * @param length Number of points in this view.
     */
    constructor(
        readonly data: Float64Array,
        readonly offset: number,
        readonly length: number,
    ) {
    }

    /**
     * Converts a schedule/global shape index to a local view index.
     *
     * @param globalIndex Absolute index in the parent {@link CompactShape}.
     * @returns Local index `globalIndex - offset`.
     */
    globalIndex( globalIndex: number ): number {
        return globalIndex - this.offset;
    }

    /**
     * Latitude at a local view index.
     *
     * @param localIndex Index in `[0, length)`.
     * @returns WGS-84 latitude in degrees.
     */
    latAt( localIndex: number ): number {
        const base = ( this.offset + localIndex ) * COMPACT_SHAPE_STRIDE;

        return this.data[base + COMPACT_SHAPE_LAT];
    }

    /**
     * Longitude at a local view index.
     *
     * @param localIndex Index in `[0, length)`.
     * @returns WGS-84 longitude in degrees.
     */
    lonAt( localIndex: number ): number {
        const base = ( this.offset + localIndex ) * COMPACT_SHAPE_STRIDE;

        return this.data[base + COMPACT_SHAPE_LON];
    }

    /**
     * Cumulative distance at a local view index.
     *
     * @param localIndex Index in `[0, length)`.
     * @returns Cumulative distance in meters from the shape start.
     */
    cumDistAt( localIndex: number ): number {
        const base = ( this.offset + localIndex ) * COMPACT_SHAPE_STRIDE;

        return this.data[base + COMPACT_SHAPE_CUM_DIST];
    }

    /**
     * Bearing×10 wire value at a local view index.
     *
     * @param localIndex Index in `[0, length)`.
     * @returns Bearing multiplied by 10.
     */
    bearingX10At( localIndex: number ): number {
        const base = ( this.offset + localIndex ) * COMPACT_SHAPE_STRIDE;

        return this.data[base + COMPACT_SHAPE_BEARING_X10];
    }

}
